/**
 * Self-contained background music button for web.
 *
 * Drop <BackgroundMusicButton /> into any screen — no Provider needed.
 * All audio management and play/pause state lives inside this component.
 *
 * Requires: npm install lottie-web
 */
import { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import lottie, { AnimationItem } from "lottie-web";

import activeMusicIcon1 from "./assets/activeMusicIcon1.json";
import disabledMusicIcon1 from "./assets/disabledMusicIcon1.json";
import disabledMusicIcon from "./assets/disabledMusicIcon.json";
import musicGlowIcon from "./assets/musicIcon_glow.json";
import clickAnimation from "./assets/ClickAnimation.json";
import defaultAudioSrc from "./assets/DanpunCiaoSweety.mp3";

const DEFAULT_VOLUME = 0.06;

// ---------------------------------------------------------------------------
// LottieIcon — mirrors <LottieView> from lottie-react-native
// ---------------------------------------------------------------------------

interface LottieIconProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  animationData: any;
  style?: CSSProperties;
  autoplay?: boolean;
  loop?: boolean;
}

function LottieIcon({
  animationData,
  style,
  autoplay = false,
  loop = false,
}: LottieIconProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    animRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop,
      autoplay,
      animationData,
    });
    return () => {
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [animationData, loop, autoplay]);

  return <div ref={containerRef} style={style} />;
}

// ---------------------------------------------------------------------------
// Shadow wrapper — mirrors CommonShadowButton
// ---------------------------------------------------------------------------

const shadowWrapStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  // borderRadius: "50%",
  // boxShadow: "0 2px 6px 0 rgba(184,197,204,0.8)",
  // backgroundColor: "white",
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface BackgroundMusicButtonProps {
  /** Override the bundled MP3 with a remote or local URL */
  audioUrl?: string;
  /** Start playing immediately on mount (default: true) */
  defaultPlaying?: boolean;
  /** Loop the track (default: true) */
  loop?: boolean;
  /** Volume 0–1 (default: 0.06) */
  volume?: number;
  /** Called whenever play/pause state changes */
  onPlayStateChange?: (isPlaying: boolean) => void;
  /** Extra styles on the root <button> */
  containerStyle?: CSSProperties;
  /** Renders the larger glowing variant */
  glowIcon?: boolean;
  /** Shrinks the disabled icon once onboarding is done */
  isOnBoardingDone?: boolean;
  /** Controlled play state — when parent sets true the button plays; false pauses */
  playing?: boolean;
}

// ---------------------------------------------------------------------------
// BackgroundMusicButton — fully self-contained
// ---------------------------------------------------------------------------

export default function BackgroundMusicButton({
  audioUrl,
  defaultPlaying = true,
  loop = true,
  volume = DEFAULT_VOLUME,
  onPlayStateChange,
  containerStyle,
  glowIcon = false,
  isOnBoardingDone = false,
  playing: controlledPlaying,
}: BackgroundMusicButtonProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const isBusyRef = useRef(false);
  const wasPlayingRef = useRef(false);

  // ── helpers ──────────────────────────────────────────────────────────────

  const updatePlayState = useCallback(
    (playing: boolean) => {
      setIsPlaying(playing);
      onPlayStateChange?.(playing);
    },
    [onPlayStateChange],
  );

  const getAudio = useCallback((): HTMLAudioElement => {
    if (audioRef.current) return audioRef.current;

    const src = audioUrl ?? defaultAudioSrc;
    const audio = new Audio(src);
    audio.loop = loop;
    audio.volume = volume;

    audio.addEventListener("play", () => updatePlayState(true));
    audio.addEventListener("pause", () => updatePlayState(false));
    audio.addEventListener("ended", () => updatePlayState(false));

    audioRef.current = audio;
    return audio;
  }, [audioUrl, loop, volume, updatePlayState]);

  const play = useCallback(async () => {
    const audio = getAudio();
    audio.loop = loop;
    audio.volume = volume;
    try {
      await audio.play();
    } catch {
      // Blocked by browser autoplay policy — music starts on first user click
    }
  }, [getAudio, loop, volume]);

  const pause = useCallback(() => {
    audioRef.current?.pause();
  }, []);

  const destroyAudio = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.src = "";
    audioRef.current = null;
    updatePlayState(false);
  }, [updatePlayState]);

  // ── toggle (called on button click) ──────────────────────────────────────

  const handleToggle = useCallback(async () => {
    if (isBusyRef.current) return;
    isBusyRef.current = true;
    try {
      if (isPlaying) pause();
      else await play();
    } finally {
      isBusyRef.current = false;
    }
  }, [isPlaying, play, pause]);

  // ── sync with controlled playing prop (e.g. tapIndicator trigger) ─────────

  useEffect(() => {
    if (controlledPlaying === undefined) return;
    if (controlledPlaying && !isPlaying) play();
    else if (!controlledPlaying && isPlaying) pause();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlledPlaying]);

  // ── auto-play on mount ────────────────────────────────────────────────────

  useEffect(() => {
    if (defaultPlaying) play();
    return () => destroyAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── re-load when audioUrl prop changes ───────────────────────────────────

  useEffect(() => {
    const wasPlaying = isPlaying;
    destroyAudio();
    if (wasPlaying) play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl]);

  // ── pause when tab is hidden; resume when visible again ──────────────────

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.hidden) {
        wasPlayingRef.current = isPlaying;
        pause();
      } else if (wasPlayingRef.current) {
        play();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [isPlaying, pause, play]);

  // ── render ────────────────────────────────────────────────────────────────

  const rootStyle: CSSProperties = {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: glowIcon ? -2 : 0,
    ...containerStyle,
  };

  return (
    <button
      onClick={handleToggle}
      style={rootStyle}
      aria-label={
        isPlaying ? "Pause background music" : "Play background music"
      }
    >
      {/* ── Standard mode ── */}
      {!glowIcon && isPlaying && (
        <span style={shadowWrapStyle}>
          <LottieIcon
            animationData={activeMusicIcon1}
            style={{ width: 32, height: 32 }}
          />
        </span>
      )}
      {!glowIcon && !isPlaying && (
        <span style={shadowWrapStyle}>
          <LottieIcon
            animationData={disabledMusicIcon1}
            style={{ width: 32, height: 32 }}
          />
        </span>
      )}

      {/* ── Glow mode ── */}
      {glowIcon && isPlaying && (
        <LottieIcon
          animationData={musicGlowIcon}
          style={{ width: 32, height: 32, marginRight: -10, marginTop: -8 }}
        />
      )}
      {glowIcon && !isPlaying && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: isOnBoardingDone ? 20 : 25,
            height: isOnBoardingDone ? 20 : 25,
          }}
        >
          <LottieIcon
            animationData={disabledMusicIcon}
            style={{ width: 20, height: 20 }}
          />
        </span>
      )}

      {/* Click-ripple overlay (glow mode only) */}
      {glowIcon && (
        <LottieIcon
          animationData={clickAnimation}
          style={{
            width: 24,
            height: 24,
            position: "absolute",
            right: -10,
            top: -2,
            pointerEvents: "none",
          }}
          autoplay={true}
          loop={true}
        />
      )}
    </button>
  );
}
